import { Injectable, computed, inject, signal } from '@angular/core';

import { ApiTesterState } from '@core/models';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ApiTesterService {
  private readonly httpsClient = inject(HttpClient);

  readonly #state = signal<ApiTesterState>({
    uri: 'https://key-provider.auth-dev.rhapsody.health/keys',
    httpMethod: 'POST',
    httpBody: '',
    requestInProgress: false,
    responseLabel: '',
    responseBody: '',
  });

  readonly state = computed(() => this.#state());
  readonly uri = computed(() => this.#state().uri);
  readonly httpMethod = computed(() => this.#state().httpMethod);
  readonly httpBody = computed(() => this.#state().httpBody);
  readonly requestInProgress = computed(() => this.#state().requestInProgress);
  readonly responseLabel = computed(() => this.#state().responseLabel);
  readonly responseBody = computed(() => this.#state().responseBody);

  public setUri(uri: string) {
    this.#state.update((state) => ({ ...state, uri }));
  }

  public setHttpMethod(httpMethod: 'POST' | 'GET' | 'PATCH' | 'PUT') {
    this.#state.update((state) => ({ ...state, httpMethod }));
  }

  public setHttpBody(httpBody: string) {
    this.#state.update((state) => ({ ...state, httpBody }));
  }

  public runTest() {
    this.setRequestInProgress(true);
    const { httpMethod } = this.#state();
    switch (httpMethod) {
      case 'POST':
        this.postCall();
        break;
      case 'GET':
        this.getCall();
        break;
      case 'PATCH':
        this.patchCall();
        break;
      case 'PUT':
        this.putCall();
        break;
    }
  }

  private getCall() {
    const { uri } = this.#state();
    this.httpsClient
      .get(uri, {
        headers: { 'Content-Type': 'application/json' },
      })
      .subscribe({
        next: (res) => {
          this.writeResponse(res);
        },
        error: (err) => {
          this.writeResponse(err, true);
          this.setRequestInProgress(false);
        },
        complete: () => {
          this.setRequestInProgress(false);
        },
      });
  }

  private postCall() {
    const { uri, httpBody } = this.#state();
    this.httpsClient
      .post(uri, httpBody, {
        headers: { 'Content-Type': 'application/json' },
      })
      .subscribe({
        next: (res) => {
          this.writeResponse(res);
        },
        error: (err) => {
          this.writeResponse(err, true);
          this.setRequestInProgress(false);
        },
        complete: () => {
          this.setRequestInProgress(false);
        },
      });
  }

  private patchCall() {
    const { uri, httpBody } = this.#state();
    this.httpsClient
      .patch(uri, httpBody, {
        headers: { 'Content-Type': 'application/json' },
      })
      .subscribe({
        next: (res) => {
          this.writeResponse(res);
        },
        error: (err) => {
          this.writeResponse(err, true);
          this.setRequestInProgress(false);
        },
        complete: () => {
          this.setRequestInProgress(false);
        },
      });
  }

  private putCall() {
    const { uri, httpBody } = this.#state();
    this.httpsClient
      .put(uri, httpBody, {
        headers: { 'Content-Type': 'application/json' },
      })
      .subscribe({
        next: (res) => {
          this.writeResponse(res);
        },
        error: (err) => {
          this.writeResponse(err, true);
          this.setRequestInProgress(false);
        },
        complete: () => {
          this.setRequestInProgress(false);
        },
      });
  }

  private setRequestInProgress(requestInProgress: boolean) {
    this.#state.update((state) => ({ ...state, requestInProgress }));
  }

  private writeResponse(res: any, error = false) {
    this.#state.update((state) => ({
      ...state,
      responseLabel: error ? 'Error' : 'Success',
      responseBody: JSON.stringify(res, null, 2),
    }));
  }

  public clearResponse() {
    this.#state.update((state) => ({
      ...state,
      responseLabel: '',
      responseBody: '',
    }));
  }
}
